import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default theme (matches current hardcoded colors)
const defaultTheme = {
  primary: '#2a9d8f',
  secondary: '#e9c46a',
  accent: '#f4a261',
  danger: '#e76f51',
  bgDark: '#264653',
  bgDarker: '#1a3a42',
  textLight: '#faf3e0',
  textMuted: '#a8c8c4',
};

// Default branding
const defaultBranding = {
  company_name: 'Westbay',
  tagline: 'CONTRACT HUNTER',
  description: 'AI-powered contract hunting for elite cloud & security professionals.',
  logo_url: 'https://customer-assets.emergentagent.com/job_gov-deal-hunt/artifacts/qbft4dvs_hero.png',
  powered_by_logo_url: 'https://customer-assets.emergentagent.com/job_530e9795-c215-4048-be9d-12bfadc67a65/artifacts/ewtqrs16_poweredby.png',
  powered_by_text: 'Poser AI™',
  powered_by_link: 'https://westbay.io',
  footer_company: 'Westbay Group LLC',
  footer_year: 2026,
};

// Default scoring profile
const defaultScoringProfile = {
  name: 'Tech/Cloud Professional',
  description: 'AWS, DevSecOps, AI, PrivateLink, GovCloud expertise',
  target_skills: ['AWS', 'DevSecOps', 'AI', 'PrivateLink', 'GovCloud'],
  categories: []
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(defaultTheme);
  const [branding, setBranding] = useState(defaultBranding);
  const [scoringProfile, setScoringProfile] = useState(defaultScoringProfile);
  const [tenantId, setTenantId] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has an organization saved with branding
    const savedOrg = localStorage.getItem('organization');
    if (savedOrg) {
      try {
        const org = JSON.parse(savedOrg);
        // If org has branding info, apply it directly
        if (org.branding) {
          applyOrgBranding(org);
          return;
        }
        // Otherwise fetch from API
        if (org.subdomain) {
          fetchOrgSettings(org.subdomain);
          return;
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
    // Default to tenant settings
    fetchTenantSettings();
  }, []);

  // Listen for organization changes (e.g., after login)
  useEffect(() => {
    const handleOrgChange = (event) => {
      const org = event.detail;
      if (org && org.branding) {
        applyOrgBranding(org);
      }
    };
    
    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, []);

  // Apply branding directly from organization object
  const applyOrgBranding = (org) => {
    const orgBranding = org.branding || {};
    
    setTenantId(org.subdomain || 'default');
    
    // Apply branding
    setBranding({
      ...defaultBranding,
      company_name: orgBranding.company_name || org.name || defaultBranding.company_name,
      tagline: orgBranding.tagline || defaultBranding.tagline,
      logo_url: orgBranding.logo_url || defaultBranding.logo_url,
    });
    
    // Apply theme colors
    const newTheme = {
      primary: orgBranding.primary_color || defaultTheme.primary,
      secondary: orgBranding.secondary_color || defaultTheme.secondary,
      accent: orgBranding.accent_color || defaultTheme.accent,
      danger: defaultTheme.danger,
      bgDark: orgBranding.background_color || defaultTheme.bgDark,
      bgDarker: defaultTheme.bgDarker,
      textLight: defaultTheme.textLight,
      textMuted: defaultTheme.textMuted,
    };
    
    setTheme(newTheme);
    applyThemeToDOM(newTheme);
    setLoading(false);
  };

  const fetchOrgSettings = async (subdomain) => {
    try {
      // Fetch organization-specific settings
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/tenant/current`, {
        headers: {
          'X-Tenant-Id': subdomain,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        applyTenantData(data, subdomain);
      } else {
        // Fallback to default
        fetchTenantSettings();
      }
    } catch (error) {
      console.error('Failed to fetch org settings:', error);
      fetchTenantSettings();
    }
  };

  const applyTenantData = (data, subdomain = 'default') => {
    setTenantId(subdomain);
    
    // Extract branding
    if (data.branding) {
      setBranding({
        ...defaultBranding,
        company_name: data.branding.company_name || defaultBranding.company_name,
        tagline: data.branding.tagline || defaultBranding.tagline,
        description: data.branding.description || defaultBranding.description,
        logo_url: data.branding.logo_url || defaultBranding.logo_url,
        powered_by_logo_url: data.branding.powered_by_logo_url || defaultBranding.powered_by_logo_url,
        powered_by_text: data.branding.powered_by_text || defaultBranding.powered_by_text,
        powered_by_link: data.branding.powered_by_link || defaultBranding.powered_by_link,
        footer_company: data.branding.footer_company || defaultBranding.footer_company,
        footer_year: data.branding.footer_year || defaultBranding.footer_year,
      });
      
      // Extract colors from branding
      const colors = data.branding.colors || {};
      setTheme({
        primary: colors.primary || data.branding.primary_color || defaultTheme.primary,
        secondary: colors.secondary || data.branding.secondary_color || defaultTheme.secondary,
        accent: colors.accent || data.branding.accent_color || defaultTheme.accent,
        danger: colors.danger || defaultTheme.danger,
        bgDark: colors.bg_dark || data.branding.bg_color || defaultTheme.bgDark,
        bgDarker: colors.bg_darker || defaultTheme.bgDarker,
        textLight: colors.text_light || defaultTheme.textLight,
        textMuted: colors.text_muted || defaultTheme.textMuted,
      });
    }
    
    // Extract scoring profile
    if (data.scoring_profile) {
      setScoringProfile({
        ...defaultScoringProfile,
        name: data.scoring_profile.name || defaultScoringProfile.name,
        description: data.scoring_profile.description || defaultScoringProfile.description,
        target_skills: data.scoring_profile.target_skills || defaultScoringProfile.target_skills,
        categories: data.scoring_profile.categories || defaultScoringProfile.categories,
      });
    }
    
    setLoading(false);
  };

  const fetchTenantSettings = async () => {
    try {
      // Use new tenant/current endpoint which handles subdomain resolution
      const res = await fetch(`${API_URL}/api/tenant/current`);
      if (res.ok) {
        const data = await res.json();
        applyTenantData(data, data.tenant_id || 'default');
        if (data.branding) {
          setBranding({
            company_name: data.branding.company_name || defaultBranding.company_name,
            tagline: data.branding.tagline || defaultBranding.tagline,
            description: data.branding.description || defaultBranding.description,
            logo_url: data.branding.logo_url || defaultBranding.logo_url,
            powered_by_logo_url: data.branding.powered_by_logo_url || defaultBranding.powered_by_logo_url,
            powered_by_text: data.branding.powered_by_text || defaultBranding.powered_by_text,
            powered_by_link: data.branding.powered_by_link || defaultBranding.powered_by_link,
            footer_company: data.branding.footer_company || defaultBranding.footer_company,
            footer_year: data.branding.footer_year || defaultBranding.footer_year,
          });

          // Extract colors and set theme
          const newTheme = {
            primary: data.branding.primary_color || defaultTheme.primary,
            secondary: data.branding.secondary_color || defaultTheme.secondary,
            accent: data.branding.accent_color || defaultTheme.accent,
            danger: data.branding.danger_color || defaultTheme.danger,
            bgDark: data.branding.background_dark || defaultTheme.bgDark,
            bgDarker: data.branding.background_darker || defaultTheme.bgDarker,
            textLight: data.branding.text_light || defaultTheme.textLight,
            textMuted: data.branding.text_muted || defaultTheme.textMuted,
          };
          setTheme(newTheme);
          applyThemeToDOM(newTheme);
        }
        
        // Extract scoring profile
        if (data.scoring_profile) {
          setScoringProfile({
            name: data.scoring_profile.name || defaultScoringProfile.name,
            description: data.scoring_profile.description || defaultScoringProfile.description,
            target_skills: data.scoring_profile.target_skills || defaultScoringProfile.target_skills,
            categories: data.scoring_profile.categories || []
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch tenant settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (themeColors) => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--color-primary', themeColors.primary);
    root.style.setProperty('--color-secondary', themeColors.secondary);
    root.style.setProperty('--color-accent', themeColors.accent);
    root.style.setProperty('--color-danger', themeColors.danger);
    root.style.setProperty('--color-bg-dark', themeColors.bgDark);
    root.style.setProperty('--color-bg-darker', themeColors.bgDarker);
    root.style.setProperty('--color-text-light', themeColors.textLight);
    root.style.setProperty('--color-text-muted', themeColors.textMuted);
    
    // Generate lighter/darker variants
    root.style.setProperty('--color-primary-hover', adjustBrightness(themeColors.primary, -15));
    root.style.setProperty('--color-primary-light', adjustBrightness(themeColors.primary, 20));
    root.style.setProperty('--color-accent-hover', adjustBrightness(themeColors.accent, -15));
  };

  const refreshTheme = () => {
    // First check localStorage for org branding
    const savedOrg = localStorage.getItem('organization');
    if (savedOrg) {
      try {
        const org = JSON.parse(savedOrg);
        if (org.branding) {
          applyOrgBranding(org);
          return;
        }
        if (org.subdomain) {
          fetchOrgSettings(org.subdomain);
          return;
        }
      } catch (e) {
        // Invalid JSON
      }
    }
    fetchTenantSettings();
  };

  // Update theme when organization changes (after login)
  const updateOrgTheme = (org) => {
    if (org && org.branding) {
      applyOrgBranding(org);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      branding, 
      scoringProfile, 
      tenantId, 
      loading, 
      refreshTheme,
      updateOrgTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Helper function to adjust color brightness
function adjustBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  r = Math.min(255, Math.max(0, r + (r * percent / 100)));
  g = Math.min(255, Math.max(0, g + (g * percent / 100)));
  b = Math.min(255, Math.max(0, b + (b * percent / 100)));
  
  // Convert back to hex
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(x).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

// Export default values for components that need fallbacks
export { defaultTheme, defaultBranding };
